import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { CountryCombobox } from "@/components/CountryCombobox";
import { useTenant } from "@/hooks/useTenant";
import { cn } from "@/lib/utils";
import { composePhone, parsePhone } from "@/lib/countryDialCodes";

interface PhoneNumberInputProps {
  countryIso: string;
  number: string;
  onCountryChange: (iso: string) => void;
  onNumberChange: (number: string) => void;
  id?: string;
  placeholder?: string;
  inputClassName?: string;
  triggerClassName?: string;
  /** "off" when an admin types someone else's number (their own would autofill). */
  autoComplete?: string;
}

// WhatsApp number field: the searchable country/dial-code picker (flag +
// code) next to a digits-only input that pops the numeric keyboard on mobile
// (inputMode="numeric" + digit stripping).
const PhoneNumberInput = ({
  countryIso,
  number,
  onCountryChange,
  onNumberChange,
  id,
  placeholder = "8888 8888",
  inputClassName,
  triggerClassName,
  autoComplete = "tel-national",
}: PhoneNumberInputProps) => (
  <div className="flex gap-2">
    <CountryCombobox
      value={countryIso}
      onChange={onCountryChange}
      showDial
      ariaLabel="Country code"
      triggerClassName={cn("rounded-xl w-[7.5rem] shrink-0", triggerClassName)}
    />
    <Input
      id={id}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete={autoComplete}
      value={number}
      onChange={(e) => onNumberChange(e.target.value.replace(/\D/g, ""))}
      placeholder={placeholder}
      className={cn("rounded-xl flex-1 min-w-0", inputClassName)}
    />
  </div>
);

interface StoredPhoneFieldProps {
  /** The stored string, e.g. "+506 88887777" (legacy bare numbers accepted). */
  value: string;
  onChange: (stored: string) => void;
  id?: string;
  placeholder?: string;
  inputClassName?: string;
  triggerClassName?: string;
  autoComplete?: string;
}

// Same field for surfaces that hold the STORED string: keeps the iso/number
// split internally and hands back the composed value, so a page can swap a
// free-text <Input value={phone}> for this without changing how it saves.
export const StoredPhoneField = ({ value, onChange, ...rest }: StoredPhoneFieldProps) => {
  const tenant = useTenant();
  const [parts, setParts] = useState(() => parsePhone(value, tenant.country));

  // Re-sync when the parent loads a value we didn't produce (an RPC prefill,
  // a form reset). Our own edits round-trip exactly, so they are skipped.
  useEffect(() => {
    if (composePhone(parts.iso, parts.number) !== (value ?? "")) setParts(parsePhone(value, tenant.country));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (next: { iso: string; number: string }) => {
    setParts(next);
    onChange(composePhone(next.iso, next.number));
  };

  return (
    <PhoneNumberInput
      {...rest}
      countryIso={parts.iso}
      number={parts.number}
      onCountryChange={(iso) => update({ iso, number: parts.number })}
      onNumberChange={(number) => update({ iso: parts.iso, number })}
    />
  );
};

export default PhoneNumberInput;
